"use client";

/**
 * Screened Badge — shown next to a lead's name when AMD detected call
 * screening (Apple Silence Unknown Callers / Google Pixel screening) on
 * a prior call.
 *
 * Click → small popover to remove the tag (false positive) or learn more.
 *
 * Effect on dialer:
 *   - DialIQ smart redial EXCLUDES these leads automatically (backend filter)
 *   - Agent can still manual-dial them anytime
 *   - "Potential Call Screening" banner appears in active call when AMD
 *     fires the same signal in real time
 */

import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { X } from "lucide-react";

// Inline custom SVG — phone receiver with a shield overlay on top.
// Communicates "this number is protected / screening calls". Drawn from
// scratch so it doesn't read as an iOS / library glyph. currentColor lets
// the parent's text-amber-700 paint the strokes; the shield body fills
// with the badge background so the phone reads as "behind" the shield.
function ScreeningIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      {/* Phone receiver */}
      <path d="M5 4.5 L9.5 4.5 L11 9 L8.5 10.5 C10 13 11 14 13.5 15.5 L15 13 L19.5 14.5 L19.5 19 C19.5 19.5 19 20 18.5 20 C11 20 4 13 4 5.5 C4 5 4.5 4.5 5 4.5 Z" />
      {/* Shield overlay top-right — fills with currentBgColor so phone is
          visibly behind it. We use 'context-fill' fallback via a paint trick:
          fill="white" works on the amber-50/100 badge bg; consumers using
          this on dark backgrounds can override via a wrapper if needed. */}
      <path
        d="M16.5 2 L20 3 L20 6 C20 7.5 18.5 9 16.5 9.5 C14.5 9 13 7.5 13 6 L13 3 L16.5 2 Z"
        fill="white"
      />
    </svg>
  );
}
import api from "@/app/_module/services/api";
import { successToast, errorToast } from "@/app/_module/services/helper";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/app/_module/components/ui/popover";

export const SCREENING_TAG = "screening_detected";

interface ScreeningBadgeProps {
  leadId: string;
  tags?: string[] | null;
  leadName?: string | null;
  compact?: boolean;
}

export function ScreeningBadge({
  leadId,
  tags,
  leadName,
  compact = false,
}: ScreeningBadgeProps) {
  // All hooks declared up front — Rules of Hooks compliance.
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);

  const removeMutation = useMutation({
    mutationFn: async () => {
      const nextTags = (tags || []).filter((t) => t !== SCREENING_TAG);
      await api.patch({
        url: `/api/leads/${leadId}`,
        body: { tags: nextTags },
      });
    },
    onSuccess: () => {
      successToast("Screening tag removed — lead is back in DialIQ");
      queryClient.invalidateQueries({ queryKey: ["leads"] });
      queryClient.invalidateQueries({ queryKey: ["redial"] });
      setOpen(false);
    },
    onError: () => errorToast("Couldn't update lead"),
  });

  const isScreened = !!tags?.includes(SCREENING_TAG);
  if (!isScreened) return null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={(e) => e.stopPropagation()}
          aria-label="Potential call screening on this number"
          className={`
            inline-flex items-center gap-1 rounded-md
            border border-amber-200 bg-amber-50 text-amber-700
            transition-all hover:border-amber-300 hover:bg-amber-100
            ${compact ? "px-1.5 py-0.5" : "px-2 py-0.5"}
          `}
        >
          <ScreeningIcon
            className={`${compact ? "h-2.5 w-2.5" : "h-3 w-3"}`}
          />
          <span
            className={`font-semibold uppercase tracking-wider ${
              compact ? "text-[9px]" : "text-[10px]"
            }`}
          >
            Screened
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-3">
        <div className="flex items-start gap-2.5 mb-2">
          <div className="flex h-7 w-7 items-center justify-center rounded-md bg-amber-100 flex-shrink-0">
            <ScreeningIcon className="h-3.5 w-3.5 text-amber-700" />
          </div>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.12em] font-bold text-amber-900">
              Potential Call Screening
            </div>
            <div className="text-xs text-gray-700 mt-0.5">
              {leadName || "This lead"} appears to use call screening.
              Excluded from DialIQ auto-dial — you can still manual-dial anytime.
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={() => removeMutation.mutate()}
          disabled={removeMutation.isPending}
          className="flex w-full items-center gap-2 rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 transition-all hover:border-slate-300 hover:bg-slate-50 disabled:opacity-50"
        >
          <X className="h-3 w-3" />
          Remove tag (false positive)
        </button>
      </PopoverContent>
    </Popover>
  );
}

export default ScreeningBadge;
